import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Single source of truth for dark background — use MAIN_BG_COLOR everywhere
const DARK_BG = '#08070B';

// Main background color - use this throughout the app
export const MAIN_BG_COLOR = DARK_BG;

// Brand color - same dark for consistency
export const BRAND_COLOR = DARK_BG;

// Theme constants
export const themeConstants = {
    border: {
        radius: '16px',
        color: 'rgba(26, 25, 48, 0.1)',
        width: '1.8px',
    },
    input: {
        radius: '16px',
    },
    shadow: {
        none: '0px 0px 0px 0px rgba(245, 245, 245, 1)',
    },
    palette: {
        background: MAIN_BG_COLOR,
        primary: {
            main: BRAND_COLOR,
            light: alpha(DARK_BG, 0.2),
            dark: DARK_BG,
            alpha: alpha(DARK_BG, 0.2),
        },
    },
    filters: {
        matte: {
            default:
                'brightness(0) saturate(100%) invert(10%) sepia(47%) saturate(620%) hue-rotate(203deg) brightness(95%) contrast(99%)',
            hover: 'grayscale(0%) brightness(100%) contrast(100%) saturate(100%)',
        },
    },

    image: {
        container: (theme: any) => ({
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            borderRadius: '16px',
            border: `0px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            position: 'relative',
            aspectRatio: '5/4',
            width: '100%',
            '&:hover': {
                '& .image-element': {
                    filter: 'grayscale(0%) brightness(100%) contrast(100%) saturate(100%)',
                },
                '& .color-overlay': {
                    opacity: 0,
                },
            },
        }),
        element: {
            objectFit: 'cover',
            width: '100%',
            height: '100%',
            filter: 'grayscale(40%)',
            transition: 'all 0.3s ease-in-out',
        },
    },
} as const;

declare module '@mui/material/styles' {
    interface Palette {
        neutral: {
            100: string;
            200: string;
            300: string;
            400: string;
            500: string;
            600: string;
            700: string;
            800: string;
            900: string;
            main: string;
            dark: string;
            light: string;
        };
    }
    interface PaletteOptions {
        neutral: {
            100: string;
            200: string;
            300: string;
            400: string;
            500: string;
            600: string;
            700: string;
            800: string;
            900: string;
            main: string;
            dark: string;
            light: string;
        };
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        secondary: true;
    }
    interface ButtonPropsSizeOverrides {
        extraLarge: true;
    }
}

const theme = createTheme({
    palette: {
        primary: {
            main: themeConstants.palette.primary.main,
            light: alpha(themeConstants.palette.primary.main, 0.2),
            dark: DARK_BG,
        },
        secondary: {
            main: '#FF6B6B',
            light: '#FFA5A5',
            dark: '#CC5555',
        },
        neutral: {
            100: '#F7F7F8',
            200: '#E8E8E9',
            300: '#DCDCDD',
            400: '#BDBDBE',
            500: '#9E9EA0',
            600: '#7E7E80',
            700: '#5F5F61',
            800: '#3F3F41',
            900: '#1C1C1E',
            main: '#9E9EA0',
            dark: '#1C1C1E',
            light: '#F7F7F8',
        },
        background: {
            default: MAIN_BG_COLOR,
            paper: MAIN_BG_COLOR,
        },
        text: {
            primary: '#1A1A1A',
            secondary: '#666666',
        },
        divider: themeConstants.border.color,
        spotify: {
            main: '#1DB954',
            light: '#1ED760',
            dark: '#1AA34A',
        },
        youtube: {
            main: '#FF0000',
            light: '#FF3333',
            dark: '#CC0000',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "DM Sans", "Arial", sans-serif',
        h1: {
            fontFamily: '"DM Serif Display", serif',
            fontSize: '2.6rem',
            fontWeight: 800,
            lineHeight: 1.4,
            color: themeConstants.palette.primary.main,
        },
        h2: {
            fontFamily: '"DM Sans", serif',
            fontSize: '1.3rem',
            fontWeight: 700,
            lineHeight: 1,
        },
        h3: {
            fontFamily: '"DM Sans", serif',
            fontSize: '1.1rem',
            fontWeight: 700,
            lineHeight: 1,
        },
        h4: {
            fontFamily: '"DM Sans", serif',
            fontSize: '1.25rem',
            fontWeight: 400,
            lineHeight: 1,
        },
        h5: {
            fontFamily: '"DM Sans", serif',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: 1,
        },
        h6: {
            fontFamily: '"DM Sans", serif',
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: 0.9,
        },
        subtitle1: {
            fontFamily: '"DM Sans", serif',
            fontSize: '0.8125rem',
            fontWeight: 500,
            lineHeight: 1.5,
        },
        subtitle2: {
            fontFamily: '"DM Sans", serif',
            fontSize: '0.75rem',
            fontWeight: 200,
            lineHeight: 1.57,
            letterSpacing: '0.5px',
        },
        body1: {
            fontSize: '0.875rem',
            lineHeight: 1.2,
        },
        body2: {
            fontSize: '0.8125rem',
            lineHeight: 1.2,
            fontWeight: 400,
        },
        button: {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
        },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    color: '#000000',
                },
                img: {
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    backgroundColor: '#ffffff',
                },
            },
        },
        MuiAvatarGroup: {
            styleOverrides: {
                root: {
                    '& .MuiAvatar-root': {
                        width: 24,
                        height: 24,
                        fontSize: '0.75rem',
                        border: '0px solid transparent',
                        bgcolor: 'transparent',
                    },
                    '& .MuiAvatarGroup-avatar': {
                        width: 24,
                        height: 24,
                        fontSize: '0.75rem',
                        color: themeConstants.palette.primary.main,
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'secondary' },
                    style: {
                        backgroundColor: 'white',
                        color: '#06060B',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                    },
                },
                {
                    props: { size: 'extraLarge' },
                    style: {
                        padding: '8px 20px',
                        fontSize: '1.25rem',
                    },
                },
            ],
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: themeConstants.border.radius,
                    border: `${themeConstants.border.width} solid ${themeConstants.border.color}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiCardMedia: {
            styleOverrides: {
                root: {
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                },
                img: {
                    borderRadius: 'inherit',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingTop: '24px',
                    paddingBottom: '24px',
                    border: '0px solid',
                    '&:last-child': {
                        paddingBottom: '20px',
                        paddingTop: '20px',
                    },
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                title: {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    color: themeConstants.palette.primary.main,
                    fontFamily: '"DM Sans", serif',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderRadius: '0px 0px 0px 0px',
                    borderLeft: 'none',
                    backgroundColor: '#F8F8F8',
                    border: `${themeConstants.border.width} solid ${themeConstants.border.color}`,
                    boxShadow: themeConstants.shadow.none,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    gap: '8px',
                    padding: '0px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    borderRadius: themeConstants.border.radius,
                    border: `${themeConstants.border.width} solid ${alpha(themeConstants.palette.primary.main, 0.1)}`,
                    '& > .MuiListItem-root': {
                        paddingTop: '8px',
                        paddingBottom: '8px',
                    },
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    transition: 'background-color 0.2s ease',
                    borderRadius: themeConstants.border.radius,
                    // border: `${themeConstants.border.width} solid ${alpha(themeConstants.palette.primary.main, 0.1)}`,
                    '&:hover': {
                        backgroundColor: alpha(themeConstants.palette.primary.main, 0.02),
                        cursor: 'pointer',
                    },
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    '&.Mui-selected': {
                        backgroundColor: themeConstants.palette.primary.main,
                        color: '#FFFFFF',
                        '&:hover': {
                            backgroundColor: themeConstants.palette.primary.main,
                        },
                    },
                    '&:hover': {
                        backgroundColor: alpha(themeConstants.palette.primary.main, 0.04),
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: themeConstants.border.color,
                    opacity: 1,
                    borderWidth: '1.2px',
                    borderRadius: themeConstants.border.radius,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: themeConstants.border.radius,
                    border: `${themeConstants.border.width} solid ${themeConstants.border.color}`,
                    boxShadow: 'none',
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    '&.Mui-active': {
                        color: themeConstants.palette.primary.main,
                        '& .MuiTableSortLabel-icon': {
                            color: themeConstants.palette.primary.main,
                        },
                    },
                    '&:hover': {
                        color: themeConstants.palette.primary.main,
                    },
                },
                icon: {
                    fontSize: '1rem',
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                maxWidthMd: {
                    maxWidth: '800px !important',
                },
                maxWidthLg: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    maxWidth: '1200px !important',
                },
                maxWidthXl: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                    maxWidth: '1400px !important',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        backgroundColor: alpha(themeConstants.palette.primary.main, 0.04),
                    },
                    '&.Mui-selected': {
                        borderRadius: '8px',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        height: '3px',
                        borderRadius: '1.5px',
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: themeConstants.input.radius,
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: `${themeConstants.border.width} solid ${themeConstants.border.color}`,
                            transition: 'all 0.2s ease-in-out',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                        },
                    },
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: themeConstants.input.radius,
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: `${themeConstants.border.width} solid ${themeConstants.border.color}`,
                            transition: 'all 0.2s ease-in-out',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                        },
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: themeConstants.input.radius,
                    transition: 'all 0.2s ease-in-out',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: `${themeConstants.border.width} solid ${themeConstants.border.color}`,
                        transition: 'all 0.2s ease-in-out',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: themeConstants.border.width,
                    },
                },
            },
        },
    },
});

export default theme;
